import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Modali, { useModali } from 'components/Modal';
import { setToastAppear } from 'components/Toast/Toast';
import { validateCardHolderName } from 'utils/cardholderValidation';
import { UpdateCardInfo } from 'api';
import * as taxiTripAPI from 'api/taxiTrip';

export interface CardHolderNamePromptRef {
  checkAndPrompt: () => boolean;
}

interface CardHolderNamePromptProps {
  onComplete: () => void;
  getSelectedCard: () => { guid: string; isEnglishName: boolean } | null;
  setSelectedCard: (card: any) => void;
}

export const CardHolderNamePrompt = forwardRef<CardHolderNamePromptRef, CardHolderNamePromptProps>(
  ({ onComplete, getSelectedCard, setSelectedCard }, ref) => {
    const [cardHolderNameInput, setCardHolderNameInput] = useState('');
    const [cardHolderNameError, setCardHolderNameError] = useState<string | null>(null);
    const [isUpdatingCardInfo, setIsUpdatingCardInfo] = useState(false);
    const hasPrompted = useRef(false);

    // 初始化 useModali
    const [modal, toggleModal] = useModali({
      animated: true,
      title: '設定持卡人英文姓名',
      overlayClose: false,
      large: true,
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setCardHolderNameInput(value);
      if (value.trim()) {
        setCardHolderNameError(validateCardHolderName(value));
      } else {
        setCardHolderNameError(null);
      }
    };

    // 使用 useRef 存取最新的 input 值，避免 handleConfirm 閉包問題
    const inputRef = useRef(cardHolderNameInput);
    useEffect(() => {
      inputRef.current = cardHolderNameInput;
    }, [cardHolderNameInput]);

    const handleConfirm = async () => {
      const currentInput = inputRef.current;
      const error = validateCardHolderName(currentInput);
      if (error) {
        setCardHolderNameError(error);
        return;
      }
      const selectedCard = getSelectedCard();
      if (!selectedCard) return;

      setIsUpdatingCardInfo(true);
      try {
        const [ecTokenData, ecTokenError] = await taxiTripAPI.GetEcToken();
        if (ecTokenError || !ecTokenData?.data?.token) {
          setToastAppear('更新失敗，請稍後再試');
          return;
        }
        const [, apiError] = await UpdateCardInfo(
          {
            cardGuid: selectedCard.guid,
            cardholderEnName: currentInput.trim(),
          },
          ecTokenData.data.token
        );
        if (apiError) {
          setToastAppear('更新失敗，請稍後再試');
        } else {
          setSelectedCard({ ...selectedCard, isEnglishName: true });
          setToastAppear('設定成功');
        }
      } catch {
        setToastAppear('更新失敗，請稍後再試');
      } finally {
        setIsUpdatingCardInfo(false);
        if ((modal as any).status()) {
          toggleModal();
        }
        setCardHolderNameInput('');
        setCardHolderNameError(null);
        onComplete();
      }
    };

    const handleCancel = () => {
      if ((modal as any).status()) {
        toggleModal();
      }
      setCardHolderNameInput('');
      setCardHolderNameError(null);
      onComplete();
    };

    // 定義按鈕
    const buttons = [
      <Modali.Button key="cancel" label="取消" isTwoButton onClick={handleCancel} />,
      <Modali.Button
        key="confirm"
        label="確認"
        isStyleDestructive
        isTwoButton
        disabled={isUpdatingCardInfo}
        onClick={handleConfirm}
      />,
    ];

    const showPrompt = () => {
      setCardHolderNameInput('');
      setCardHolderNameError(null);
      toggleModal();
    };

    useImperativeHandle(ref, () => ({
      checkAndPrompt: () => {
        const selectedCard = getSelectedCard();
        if (selectedCard && !selectedCard.isEnglishName && !hasPrompted.current) {
          hasPrompted.current = true;
          showPrompt();
          return true;
        }
        hasPrompted.current = false;
        return false;
      },
    }));

    return (
      <Modali.Modal {...modal} options={{ ...modal.options, buttons }}>
        <div className="card-holder-name-modal__content">
          <input
            className={`card-holder-name-modal__input ${
              cardHolderNameError ? 'card-holder-name-modal__input--error' : ''
            }`}
            placeholder="請輸入信用卡面上的英文姓名"
            value={cardHolderNameInput}
            onChange={handleChange}
            maxLength={60}
            autoComplete="off"
          />
          {cardHolderNameError && (
            <p className="card-holder-name-modal__error">{cardHolderNameError}</p>
          )}
          <p className="card-holder-name-modal__note">
            為加速您的刷卡交易驗證流程，請您輸入信用卡上的英文姓名。即使您選擇不輸入，系統仍會繼續結帳，但部分銀行可能會因未提供而影響交易成功率
          </p>
        </div>
      </Modali.Modal>
    );
  }
);
